import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Mail, Phone, MapPin, Send, Clock, MessageSquare } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

export const Route = createFileRoute('/contact')({
  component: ContactPage,
})

const services = [
  { value: 'website', label: 'Website Development' },
  { value: 'software', label: 'Software Development' },
  { value: 'mobile', label: 'Mobile App Development' },
  { value: 'ai', label: 'AI & API Integration' },
  { value: 'other', label: 'Other / Not Sure' },
]

const budgetRanges = [
  { value: 'under-50k', label: 'Under ৳50,000' },
  { value: '50k-100k', label: '৳50,000 - ৳100,000' },
  { value: '100k-300k', label: '৳100,000 - ৳300,000' },
  { value: '300k-500k', label: '৳300,000 - ৳500,000' },
  { value: 'above-500k', label: 'Above ৳500,000' },
  { value: 'not-sure', label: 'Not Sure Yet' },
]

const contactInfo = [
  {
    icon: Mail,
    title: 'Email',
    value: 'hello@logicnext.dev',
    description: "We'll respond within 24 hours",
  },
  {
    icon: Phone,
    title: 'Phone',
    value: '+880 1XXX-XXXXXX',
    description: 'Sat-Thu, 10AM-7PM BST',
  },
  {
    icon: MapPin,
    title: 'Location',
    value: 'Dhaka, Bangladesh',
    description: 'Remote-first team',
  },
]

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    budget: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Simulate form submission
    console.log('Form submitted:', formData)

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000))

    setIsSubmitting(false)
    setIsSubmitted(true)

    // Reset form after delay
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        budget: '',
        message: '',
      })
      setIsSubmitted(false)
    }, 3000)
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <>
      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <p className="text-sm font-medium text-indigo-400">Contact Us</p>
          <h1 className="text-4xl md:text-6xl font-bold">
            Let's Start a Conversation
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Have a project in mind? We'd love to hear about it. Fill out the
            form below and we'll get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative z-10 py-16 container mx-auto px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-slate-900/30 border border-white/5 rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-8">
                <MessageSquare className="h-6 w-6 text-indigo-400" />
                <h2 className="text-2xl font-bold">Send Us a Message</h2>
              </div>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
                    <Send className="h-8 w-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-slate-400">
                    We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        required
                        className="bg-slate-900/50 border-white/10 focus:border-indigo-500"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@example.com"
                        required
                        className="bg-slate-900/50 border-white/10 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+880 1XXX-XXXXXX"
                        className="bg-slate-900/50 border-white/10 focus:border-indigo-500"
                      />
                    </div>

                    {/* Service */}
                    <div className="space-y-2">
                      <Label>Service Interested In *</Label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) =>
                          setFormData((prev) => ({ ...prev, service: value }))
                        }
                        required
                      >
                        <SelectTrigger className="bg-slate-900/50 border-white/10">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem
                              key={service.value}
                              value={service.value}
                            >
                              {service.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="space-y-2">
                    <Label>Estimated Budget</Label>
                    <Select
                      value={formData.budget}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, budget: value }))
                      }
                    >
                      <SelectTrigger className="bg-slate-900/50 border-white/10">
                        <SelectValue placeholder="Select your budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetRanges.map((budget) => (
                          <SelectItem key={budget.value} value={budget.value}>
                            {budget.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message">Project Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your project, goals, and any specific requirements..."
                      required
                      rows={5}
                      className="bg-slate-900/50 border-white/10 focus:border-indigo-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Send Message
                        <Send className="h-4 w-4" />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Cards */}
            {contactInfo.map((info, index) => {
              const Icon = info.icon
              return (
                <div
                  key={index}
                  className="bg-slate-900/30 border border-white/5 rounded-2xl p-6 hover:border-white/10 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/10 shrink-0">
                      <Icon className="h-6 w-6 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">
                        {info.title}
                      </h3>
                      <p className="text-slate-300">{info.value}</p>
                      <p className="text-slate-500 text-sm mt-1">
                        {info.description}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}

            {/* Response Time */}
            <div className="bg-linear-to-br from-indigo-500/10 to-blue-500/10 border border-indigo-500/20 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="h-5 w-5 text-indigo-400" />
                <h3 className="font-semibold text-white">Quick Response</h3>
              </div>
              <p className="text-slate-400 text-sm">
                We typically respond to inquiries within 24 hours during
                business days. For urgent matters, please call us directly.
              </p>
            </div>

            {/* FAQ Link */}
            <div className="bg-slate-900/30 border border-white/5 rounded-2xl p-6">
              <h3 className="font-semibold text-white mb-2">
                Common Questions?
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Check our FAQ section on the homepage for quick answers to
                frequently asked questions.
              </p>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-white/10 hover:bg-white/5"
              >
                <Link to="/" hash="faq">
                  View FAQ
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
